// src/hooks/useChat.js
import { useState, useEffect, useRef, useContext } from "react";
import { AuthContext } from "../contexts/AuthContext";
import { fetchChatAudio } from "../api/fetchChatAudio";
import { PCMPlayerManager } from "../utils/PCMPlayerManager";
import { Bounce, toast } from 'react-toastify';
import {
  createConversation,
  updateConversation,
} from "../api/conversationsApi";
import { fetchWeather } from "../api/fetchWeather";
import { useChatContext } from "../contexts/ChatContext";

export function useChat() {
  const { messages, addMessage, setMessages } = useChatContext();
  
  const [userInput, setUserInput] = useState("");
  const [model, setModel] = useState("openai-text-only");
  const [isRecording, setIsRecording] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  // NEW state to indicate text-only retrieval in progress.
  const [isRetrieving, setIsRetrieving] = useState(false);
  const { chatStyle, profileLoaded, user, allowed_domains } =
    useContext(AuthContext);

  // New state to store the conversation ID returned by the backend.
  const [chatId, setchatId] = useState(null);

  const pcmManagerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const elapsedTimeRef = useRef(null);
  const greetingFetchedRef = useRef(false);

  useEffect(() => {
    if (!pcmManagerRef.current) {
      pcmManagerRef.current = new PCMPlayerManager({
        onPlaybackEnd: () => setIsPlayingAudio(false),
      });
    }
    return () => {
      pcmManagerRef.current?.destroy();
      pcmManagerRef.current = null;
    };
  }, []);

  // Only fire the greeting after profile has loaded and if there are no messages.
  useEffect(() => {
    if (profileLoaded && !greetingFetchedRef.current && messages.length === 0) {
      greetingFetchedRef.current = true;
      fetchGreeting();
    }
  }, [profileLoaded, messages]);

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  async function fetchGreeting() {
    // 1. Friendly greeting based on username from the Supabase client.
    // let friendlyGreeting = `Hi ${user?.user_metadata?.display_name || "there"}, I'm here to help you with anything related to news. How are you doing today? Any specific news category or event you want to know more about?\n\n`;
    let friendlyGreeting = `Hi ${user?.user_metadata?.display_name || "there"}! Here’s what’s happening today — starting with the weather and a quick look at AI & Tech news. Curious about something? Just ask!\n\n`;	
    // friendlyGreeting += "#### _Updates v0.6 → v0.7_\n\n";
    // friendlyGreeting += "* 1. _Tap an article title 📄 to see extra details._\n";
    // friendlyGreeting += "* 2. _Personal Profile: User preferences expressed in the conversation in text are now being captured and stored to tailor future experiences_ 🤖💡\n\n";
    // friendlyGreeting += "* 3. _Audio Assistant: you can now have a spoken conversation with NewsBuddy!_ 🎤\n\n";
    // friendlyGreeting += "#### _What to test_\n\n";
    // friendlyGreeting += "1. _Chat on different topics and tap article titles for more details._\n";
    // friendlyGreeting += "2. _Try using the Audio Assistant to have a spoken conversation._\n\n";
    // friendlyGreeting += "#### _Known bugs_\n\n";
    // friendlyGreeting += "* 1. _'Tap on' triggers on all bold text._\n";
    // friendlyGreeting += "* 2. _Occasional missing suggested questions._\n";
    // friendlyGreeting += "* 3. _No streaming text responses._\n\n";
    // friendlyGreeting += "_Cheers and enjoy! 🚀_\n";
    // friendlyGreeting += "---";
    
    

    
    
    setMessages((prev) => [
      ...prev,
      { role: "assistant", content: friendlyGreeting },
    ]);

    // 2. Optionally, fetch weather info and add it with emoticons.
    const weatherTask = (async () => {
      try {
        // Fetch weather info concurrently.
        const weatherData = await fetchWeather({
          latitude: 50.87959,
          longitude: 4.70093,
        });
        // Append the weather message as soon as it’s available.
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: weatherData.message + "\n\n",
          },
        ]);
      } catch (error) {
        console.error("Error fetching weather info:", error);
      }
    })();

    const chatAudioTask = (async () => {
      // (Optional) You can show a “news summary” message before streaming.
      // setMessages((prev) => [
      //   ...prev,
      //   {
      //     role: "assistant",
      //     content:
      //       "Let me fetch you your news summary in tech news for today.\n\n\n",
      //   },
      // ]);
  
      // Set the UI state to indicate streaming.
      setIsTyping(true);
      setIsPlayingAudio(true);
      abortControllerRef.current = new AbortController();
  
      const greetingRequest = {
        messages: [],
        personal_context: user?.username || "Guest",
      };
  
      await sleep(300);

      try {
        await fetchChatAudio(
          greetingRequest.messages,
          handleTextChunk,
          handleAudioChunk,
          "/chataudio/openai-text-only-with-insights",
          abortControllerRef.current,
          handleSourcesChunk,
          handleActionsChunk,
          { chatStyle, chatId, allowed_domains }
        );
      } catch (err) {
        if (err.name === "AbortError") {
          console.log("Greeting fetch aborted by user");
        } else {
          console.error("Error streaming greeting audio:", err);
          toast.error("Oops! We encountered a problem connecting to the API. Please try again later.", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
            transition: Bounce,
            });
        }        
      } finally {
        setIsTyping(false);
        setIsPlayingAudio(false);
        abortControllerRef.current = null;
        pcmManagerRef.current?.flush();
        // Optionally, save the conversation after the streaming is complete.
        saveConversation([...messages]);
      }
    })();
  
    // Optionally wait for both tasks to complete (if your flow requires it).
    await Promise.all([weatherTask, chatAudioTask]);
  }

  // Main send function for user-initiated messages.
  async function handleSend(inputOverride) {
    const tempUserInput = inputOverride || userInput;
    if (typeof tempUserInput !== "string" || !tempUserInput.trim()) return;
    const newUserMessage = { role: "user", content: tempUserInput };
    setMessages((prev) => [...prev, newUserMessage]);
    setUserInput("");
    await sendChatAudio([...messages, newUserMessage]);
  }

  async function sendChatAudio(completeMessages) {
    setIsTyping(true);
    // For text-only models, indicate retrieval instead of playing audio.
    if (model === "openai-text-only") {
      setIsRetrieving(true);
    } else {
      setIsPlayingAudio(true);
    }
    abortControllerRef.current = new AbortController();

    try {
      await fetchChatAudio(
        completeMessages,
        handleTextChunk,
        handleAudioChunk,
        getEndpointForModel(model),
        abortControllerRef.current,
        handleSourcesChunk,
        handleActionsChunk,
        { chatStyle, chatId, allowed_domains }
      );
    } catch (err) {
      if (err.name === "AbortError") {
        console.log("fetchChatAudio aborted by user");
      } else {
        console.error("API request failed:", err);
        toast.error("Oops! We encountered a problem connecting to the API. Please try again later.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
          });
      }
    } finally {
      setIsTyping(false);
      if (model === "openai-text-only") {
        setIsRetrieving(false);
      } else {
        setIsPlayingAudio(false);
      }
      abortControllerRef.current = null;
      pcmManagerRef.current?.flush();
      saveConversation(completeMessages);
    }
  }

  // Save or update the conversation using our API client.
  async function saveConversation(conversation) {
    if (!user) return;
    const payload = {
      messages: conversation,
      user_id: user.id,
    };
    try {
      if (!chatId) {
        // Create new conversation record.
        const data = await createConversation(payload);
        if (data && data.id) {
          setchatId(data.id);
        }
      } else {
        // Update the existing conversation.
        await updateConversation(chatId, payload);
      }
    } catch (error) {
      console.error("Error saving conversation", error);
    }
  }

  // Text callback.
  function handleTextChunk(partialText) {
    setIsTyping(false);
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (!last || last.role !== "assistant") {
        return [...prev, { role: "assistant", content: partialText }];
      }
      return [
        ...prev.slice(0, -1),
        { ...last, content: last.content + partialText },
      ];
    });
  }

  // Audio callback.
  function handleAudioChunk(pcmData) {
    setIsTyping(false);
    pcmManagerRef.current?.feed(pcmData);
  }

  // Sources callback.
  function handleSourcesChunk(sourcesArray) {
    setMessages((prev) => {
      if (!prev.length) return prev;
      const lastMsg = prev[prev.length - 1];
      if (lastMsg.role === "assistant") {
        return [...prev.slice(0, -1), { ...lastMsg, sources: sourcesArray }];
      }
      return [
        ...prev,
        { role: "assistant", content: "", sources: sourcesArray },
      ];
    });
  }

  // Actions callback.
  function handleActionsChunk(actionsArray) {
    setMessages((prev) => {
      if (!prev.length) return prev;
      const lastMsg = prev[prev.length - 1];
      if (lastMsg.role === "assistant") {
        return [
          ...prev.slice(0, -1),
          { ...lastMsg, next_actions: actionsArray },
        ];
      }
      return prev;
    });
  }

  function getEndpointForModel(modelValue) {
    switch (modelValue) {
      case "openai-text-only":
        return "/chataudio/openai-text-only";
      case "openai-text-only-with-insights":
        return "/chataudio/openai-text-only-with-insights";
      case "openai":
        return "/chataudio/openai";
      case "openai-live":
        return "/chataudio/openai-live";
      case "gemini":
        return "/chataudio/gemini";
      default:
        return "/chataudio/openai";
    }
  }

  // Recording controls.
  function startRecording() {
    setIsRecording(true);
    startElapsedTime();
  }
  function stopRecording() {
    setIsRecording(false);
    stopElapsedTime();
  }
  function startElapsedTime() {
    setElapsedTime(0);
    elapsedTimeRef.current = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);
  }
  function stopElapsedTime() {
    if (elapsedTimeRef.current) {
      clearInterval(elapsedTimeRef.current);
      elapsedTimeRef.current = null;
    }
  }

  // NEW: Function to stop text-only retrieval.
  function handleStopRetrieval() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsRetrieving(false);
    }
  }

  return {
    messages,
    addMessage,
    setMessages,
    userInput,
    setUserInput,
    model,
    setModel,
    isRecording,
    isPlayingAudio,
    elapsedTime,
    isTyping,
    handleSend,
    startRecording,
    stopRecording,
    // Return new properties for text-only retrieval.
    isRetrieving,
    handleStopRetrieval,
    chatId,
  };
}
